import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { FLOAT_TEXTURES_OK } from '../float-textures.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract: forbidden. The one light here is the grazing lamp
// that gives the sand its relief, and it is written into the shaders; a rig
// from above would flatten every ridge it draws.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'chladni',
  family: 'particles',
  danceability: 0.50,
  requiresTier: 1,
  requiresFloatTextures: true,
  energyLevel: 0.45,
  bpmMin: 60,
  bpmMax: 150,
  movement: 0.45,
  tonality: 0.70,
  prefers: { dynamics: [0.20, 1.0], percussiveness: [0.20, 0.90] },
  enabled: FLOAT_TEXTURES_OK
};

// Chladni: pale sand on a black metal plate that vibrates, under a grazing
// light. The grains flee the parts that move and gather on the nodal lines,
// drawing the figure of the mode - and the figure changes with the music.
//
// THE DECISIVE DETAIL: THE FIGURE SNAPS. When the plate goes from one
// resonance to the next the pattern does not slide into the new one: the
// old lines are no longer still, every grain sitting on them is thrown off
// in a fraction of a second, and the sand regroups on the new lines in one
// to two seconds. That is physics, and it is what says a note has changed.
// Here it comes for free from the model - a grain on an old node sits on a
// new antinode, where the agitation is highest - and is pushed by a short
// dispersal pulse on top so it reads as a burst, not a drift.
//
// THE MODEL. A square plate, free edges, the classical approximation
//   z(x, y) = cos(n pi x) cos(m pi y) - cos(m pi x) cos(n pi y),  x, y in [0, 1]
// and, in the circular variant, z(r, t) = J_n(k r) cos(n t) with the Bessel
// function summed from its series. The nodal lines are z = 0. Each grain
// moves down the gradient of |z| (toward the lines), is agitated in
// proportion to |z| (a grain on an antinode jumps, one on a node is still),
// and is braked hard: the motion is overdamped, as sand on steel is. A low-
// resolution density texture adds a slight repulsion between neighbours, so
// a line thickens into a ridge instead of thinning into a thread.
//
// 40 000 grains in float ping-pong textures (Swarm's infrastructure), one
// pass per fixed step. Float textures are only ever read at their own texel
// centres (NEAREST), which is all the Pi can do; the density is 8-bit and
// may be filtered.

const TIER = {
  // Tier 0 is the live-downgrade refuge: the orchestrator never offers the
  // piece below tier 1 (requiresTier).
  0: { n: 90, shadows: false, circle: false },
  1: { n: 142, shadows: true, circle: false },
  2: { n: 200, shadows: true, circle: true }
};
const DEFAULT_TIER = 2;

// --- the modes -----------------------------------------------------------------
// Sixteen (n, m), n != m, from (1, 2) to (5, 8), sorted by complexity: the
// simple ones give crosses and squares, the high ones lace.
const MODES = [[1, 2], [1, 3], [2, 3], [1, 4], [2, 5], [3, 4], [3, 5], [2, 6], [4, 5], [3, 6], [4, 6], [3, 7], [4, 7], [5, 6], [5, 7], [5, 8]];
// The circular variant: (n, k) - angular order and radial wavenumber. k is
// a zero of J_n divided by 0.8, so the OUTERMOST nodal circle sits at
// r = 0.8: beyond it |z| grows toward the rim and the gradient drains that
// band inward. With the zero on the rim itself (the first choice) the sand
// gathered in a ring along the edge, which no plate shows.
const CIRCLE_MODES = [[0, 6.9], [1, 4.8], [2, 6.4], [1, 8.8], [0, 10.8], [2, 10.5], [4, 9.5], [3, 12.2]];
const CIRCLE_CHANCE = 0.35;      // of a phrase change, at tier 2

// --- the sand ------------------------------------------------------------------
const FIXED_DT = 1 / 60;
const MAX_SUBSTEPS = 2;
const FORCE = 0.75;              // plate units per second down the gradient of |z|: regroups in 1-2 s
const FORCE_BEAT = 1.6;          // ... times this on the beat: the lines sharpen
const AGITATION = 0.55;          // the random walk on an antinode, plate units/s at |z| = 1
const AGITATION_BEAT = 1.4;      // the beat's agitation on the antinodes, over AGITATION
// THE SHIVER: on the beat every grain moves, nodes included - the agitation
// above is zero on a node, so without this the settled figure never
// flinched. Plate units/s at the kick's peak, over ~0.15 s: a few pixels.
const SHIVER = 0.30;
const BEAT_DECAY = 7.0;          // per second
const DISPERSE_NEAR = 0.9;       // the burst on a neighbouring mode
const DISPERSE_FAR = 1.6;        // ... and on a far one
const DISPERSE_DECAY = 3.4;
const DISPERSE_SPREAD = 1.8;     // plate units/s at pulse 1, regardless of |z|
const DROP_AIR = 2.2;            // everything in the air for a beat
const REPEL = 2.5;               // down the density gradient: a ridge, not a thread
const DENSITY_RES = 96;
// 0.005: ~200 grains saturate a texel. At 0.014 a line's core saturated and
// the gradient there was flat, so the repulsion could not open the line.
const DENSITY_ADD = 0.005;
const RIM = 0.985;               // the plate's usable half-extent

// --- the bounce ------------------------------------------------------------------
const BOUNCE_H = 0.16;           // plate units, at full agitation on an antinode
const BOUNCE_LOW = 0.6;          // bands.low swings it by this much
const BOUNCE_HZ = [2.0, 5.0];

// --- the render ------------------------------------------------------------------
const PLATE_HALF = 1.0;
const CAMERA_PITCH = 50 * Math.PI / 180;   // looking down at 50 degrees
const CAMERA_DIST = 2.85;
const CAMERA_FOV = 40;
const LIGHT_AZIMUTH = 0.35;      // radians, from the right and a little in front
const LIGHT_ELEV = 14 * Math.PI / 180;     // grazing
const SAND = [0.86, 0.78, 0.60];
const SAND_HUE_VAR = 0.05;       // +-5% per grain
const PALETTE_IN_LIGHT = 0.30;   // the palette tints the lamp, at 30%
const GRAIN_PX = 2.6;            // on a 720-line buffer, at the plate's centre
const SHADOW_PX = 3.4;
const SHADOW_ALPHA = 0.55;
const PLATE_BASE = 0.030;        // linear: brushed black steel
const RIM_WIDTH = 0.018;
const VIBE_BLUR = 0.012;         // the plate's edges blur while the sand is in the air

const SAFE_FLOOR = 0.50;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADERS
// =============================================================================

// The mode, as a function of a point on the plate (x, y in [-1, 1]).
// uMode = (n, m, shape, k): shape 0 is the square (n, m), shape 1 the circle
// (angular order n, wavenumber k). Returns |z| and the gradient of |z|, both
// scaled so a force reads the same across the library.
const MODE_CHUNK = `
  uniform vec4 uMode;

  // J_n(x) from its series, the terms by recurrence so nothing overflows:
  // t0 = (x/2)^n / n!, t_{k+1} = t_k * (-(x/2)^2) / ((k+1)(n+k+1)). Twenty
  // terms hold to x = 12, which is past the rim at every k in the library.
  float besselJ(float n, float x) {
    float h = x * 0.5;
    float t = 1.0;
    for (int i = 1; i <= 4; i++) { if (float(i) <= n) t *= h / float(i); }
    float s = t;
    float h2 = -h * h;
    for (int k = 0; k < 20; k++) {
      t *= h2 / ((float(k) + 1.0) * (n + float(k) + 1.0));
      s += t;
    }
    return s;
  }
  float modeZ(vec2 p) {
    if (uMode.z < 0.5) {
      vec2 q = p * 0.5 + 0.5;
      float n = uMode.x, m = uMode.y;
      return cos(n * 3.14159265 * q.x) * cos(m * 3.14159265 * q.y) - cos(m * 3.14159265 * q.x) * cos(n * 3.14159265 * q.y);
    }
    float r = length(p);
    float th = atan(p.y, p.x);
    return besselJ(uMode.x, uMode.w * r) * cos(uMode.x * th);
  }
  // |z| and its gradient. The square's gradient is analytic; the circle's is
  // a central difference (the series costs the same as a cosine pair).
  vec3 modeAbs(vec2 p) {
    float z = modeZ(p);
    vec2 g;
    if (uMode.z < 0.5) {
      vec2 q = p * 0.5 + 0.5;
      float n = uMode.x, m = uMode.y, P = 3.14159265;
      g.x = -n * P * sin(n * P * q.x) * cos(m * P * q.y) + m * P * sin(m * P * q.x) * cos(n * P * q.y);
      g.y = -m * P * cos(n * P * q.x) * sin(m * P * q.y) + n * P * cos(m * P * q.x) * sin(n * P * q.y);
      g *= 0.5 / (P * max(n, m));          // d/dp, normalised by the mode's own scale
    } else {
      float e = 0.004;
      g = vec2(modeZ(p + vec2(e, 0.0)) - modeZ(p - vec2(e, 0.0)), modeZ(p + vec2(0.0, e)) - modeZ(p - vec2(0.0, e))) / (2.0 * e);
      g *= 1.0 / uMode.w;
    }
    return vec3(abs(z), g * sign(z));
  }
`;

const quadVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

// THE STEP. One grain per texel: xy its position on the plate, z a per-grain
// seed, w unused. Overdamped: the position moves by the force times dt and
// keeps no velocity, which is the strong friction of sand on steel and what
// stops a grain dead once it reaches a line.
const stepFragmentShader = `
  precision highp float;
  uniform sampler2D uPos;
  uniform sampler2D uDensity;
  uniform float uDt;
  uniform float uTime;
  uniform float uForce;
  uniform float uAgitation;
  uniform float uDisperse;
  uniform float uShiver;
  uniform float uRepel;
  uniform float uCircle;
  varying vec2 vUv;
  ${MODE_CHUNK}

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }

  void main() {
    vec4 P = texture2D(uPos, vUv);
    vec2 p = P.xy;
    float seed = P.z;
    vec3 za = modeAbs(p);

    // Down the gradient of |z|, toward the nodal lines. The slope is
    // normalised: a grain far from a line moves at the full speed, one close
    // to it slows, so it does not overshoot and oscillate across the line.
    float gl2 = dot(za.yz, za.yz);
    vec2 dir = gl2 > 1e-8 ? za.yz * inversesqrt(gl2) : vec2(0.0);
    float slope = min(1.0, sqrt(gl2) * 3.0);
    vec2 v = -dir * (uForce * slope * min(1.0, za.x * 2.5));

    // The agitation: a random walk whose amplitude IS |z|. On a node it is
    // zero; on an antinode the grain jumps. A new direction every frame.
    float a = hash12(vec2(seed * 97.0, uTime * 61.0)) * 6.2831853;
    vec2 rnd = vec2(cos(a), sin(a));
    v += rnd * (uAgitation * za.x);
    // The dispersal pulse: everything moves, whatever |z| - the old lines are
    // no longer still.
    v += rnd * (uDisperse * ${DISPERSE_SPREAD.toFixed(2)} + uShiver);

    // The neighbours: down the density gradient, so a crowded line spreads
    // into a ridge rather than piling into a thread.
    vec2 duv = p * 0.5 + 0.5;
    float e = 1.0 / ${DENSITY_RES.toFixed(1)};
    vec2 gd = vec2(texture2D(uDensity, duv + vec2(e, 0.0)).r - texture2D(uDensity, duv - vec2(e, 0.0)).r,
                   texture2D(uDensity, duv + vec2(0.0, e)).r - texture2D(uDensity, duv - vec2(0.0, e)).r);
    v -= gd * uRepel;

    p += v * uDt;

    // Contained by the rim - a square, or the disc of the circular variant.
    // THE DISC PUSHES BACK near its edge: the outermost antinode's gradient
    // points outward, and without this the grains it throws off piled up on
    // the rim in a ring the figure never has.
    if (uCircle > 0.5) {
      float r = length(p);
      float edge = smoothstep(0.80, ${RIM.toFixed(3)}, r);
      p -= (p / max(r, 1e-4)) * (edge * 1.5 * uDt);
      if (r > ${RIM.toFixed(3)}) p *= ${RIM.toFixed(3)} / r;
    } else {
      p = clamp(p, -${RIM.toFixed(3)}, ${RIM.toFixed(3)});
    }
    gl_FragColor = vec4(p, seed, 1.0);
  }
`;

// The density: every grain as a small additive point into a DENSITY_RES
// square over the plate.
const densityVertexShader = `
  precision highp float;
  uniform sampler2D uPos;
  void main() {
    vec2 p = texture2D(uPos, position.xy).xy;
    gl_Position = vec4(p, 0.0, 1.0);
    gl_PointSize = 2.0;
  }
`;
const densityFragmentShader = `
  precision mediump float;
  void main() { gl_FragColor = vec4(${DENSITY_ADD.toFixed(3)}); }
`;

// THE GRAINS. Placed on the plate at their simulated position, raised by the
// bounce, lit by the grazing lamp through the ridge they sit on (the density
// gradient is the ridge's slope: the side toward the lamp is lit, the other
// shaded), and each one has a lit side and a dark side of its own.
function grainVertexShader(shadow) {
  return `
  precision highp float;
  uniform sampler2D uPos;
  uniform sampler2D uDensity;
  uniform float uTime;
  uniform float uPixel;        // grain size scale (buffer height / 720)
  uniform float uBounce;       // bounce amplitude, plate units
  uniform float uAgitation;    // the agitation that lifts the grains
  uniform float uAir;          // the drop: everything up
  uniform vec3 uLight;         // direction TOWARD the lamp, world
  uniform vec3 uLightCol;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec3 vCol;
  varying float vAlpha;
  varying vec2 vLightScreen;
  ${MODE_CHUNK}

  void main() {
    vec4 P = texture2D(uPos, position.xy);
    vec2 p = P.xy;
    float seed = P.z;
    vec3 za = modeAbs(p);

    // The bounce: a grain on an antinode hops at its own rhythm, one on a
    // node lies still. Rectified sine, so it lands and waits.
    float hz = mix(${BOUNCE_HZ[0].toFixed(1)}, ${BOUNCE_HZ[1].toFixed(1)}, fract(seed * 7.3));
    float hop = max(0.0, sin(uTime * hz * 6.2831853 + seed * 6.2831853));
    float lift = min(1.0, za.x * 2.0) * uAgitation + uAir;
    float h = uBounce * hop * min(1.0, lift) * (0.6 + 0.4 * fract(seed * 3.7));

    vec3 world = vec3(p.x * ${PLATE_HALF.toFixed(1)}, h + 0.004, p.y * ${PLATE_HALF.toFixed(1)});
${shadow ? `
    // THE SHADOW: the grain's dark print on the plate, pushed away from the
    // lamp, further the higher the grain is.
    vec2 away = -normalize(uLight.xz);
    float reach = 0.006 + h * (uLight.y > 0.05 ? length(uLight.xz) / uLight.y : 4.0);
    world = vec3(p.x + away.x * reach, 0.002, p.y + away.y * reach);
` : ''}
    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    float depth = max(-mv.z, 0.001);
    gl_PointSize = ${shadow ? SHADOW_PX.toFixed(1) : GRAIN_PX.toFixed(1)} * uPixel * ${CAMERA_DIST.toFixed(2)} / depth;

    // The ridge: the sand's slope from the density, lit by the lamp.
    vec2 duv = p * 0.5 + 0.5;
    float e = 1.0 / ${DENSITY_RES.toFixed(1)};
    vec2 gd = vec2(texture2D(uDensity, duv + vec2(e, 0.0)).r - texture2D(uDensity, duv - vec2(e, 0.0)).r,
                   texture2D(uDensity, duv + vec2(0.0, e)).r - texture2D(uDensity, duv - vec2(0.0, e)).r);
    vec3 N = normalize(vec3(-gd.x * 2.2, 0.35, -gd.y * 2.2));
    float ridge = 0.45 + 0.55 * max(0.0, dot(N, uLight));

    // Pale sand, +-5% of hue per grain, in the lamp's colour.
    float hv = (fract(seed * 11.3) - 0.5) * 2.0 * ${SAND_HUE_VAR.toFixed(2)};
    vec3 sand = vec3(${SAND[0].toFixed(2)} * (1.0 + hv), ${SAND[1].toFixed(2)}, ${SAND[2].toFixed(2)} * (1.0 - hv));
    vec3 col = sand * uLightCol * ridge;
    // In the air the grain catches the lamp fully.
    col *= 1.0 + 0.6 * min(1.0, h / max(uBounce, 0.001));

    // The lamp's direction on screen, for the grain's own lit side.
    vec4 lc = projectionMatrix * modelViewMatrix * vec4(world + uLight * 0.1, 1.0);
    vLightScreen = normalize(lc.xy / max(lc.w, 0.0001) - clip.xy / max(clip.w, 0.0001) + vec2(1e-5));

    vec2 ndc = clip.xy / max(clip.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
    vAlpha = ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * (${((1 - SAFE_FLOOR) / 0.68).toFixed(5)});
    vCol = col;
  }
`;
}
const grainFragmentShader = `
  precision mediump float;
  varying vec3 vCol;
  varying float vAlpha;
  varying vec2 vLightScreen;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    c.y = -c.y;
    float r2 = dot(c, c);
    if (r2 > 0.25) discard;
    // a lit side toward the lamp, a dark side away from it
    float side = 0.62 + 0.38 * clamp(dot(normalize(c + vec2(1e-5)), vLightScreen) * 2.0 * sqrt(r2) * 2.0, -1.0, 1.0);
    float edge = 1.0 - smoothstep(0.16, 0.25, r2);
    gl_FragColor = vec4(vCol * side * vAlpha, edge);
  }
`;
const shadowFragmentShader = `
  precision mediump float;
  varying float vAlpha;
  void main() {
    vec2 c = gl_PointCoord - 0.5;
    float r2 = dot(c, c);
    if (r2 > 0.25) discard;
    float edge = 1.0 - smoothstep(0.10, 0.25, r2);
    gl_FragColor = vec4(0.0, 0.0, 0.0, edge * ${SHADOW_ALPHA.toFixed(2)} * vAlpha);
  }
`;

// THE PLATE: brushed black steel, a thin rim, a mounting rod at the centre.
// ?chladniz=1 shows |z| of the current mode in grey instead: the dark lines
// are where the sand should gather.
const plateVertexShader = `
  precision highp float;
  varying vec2 vP;
  varying vec4 vClip;
  void main() {
    vP = position.xz;
    vClip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = vClip;
  }
`;
const plateFragmentShader = `
  precision highp float;
  uniform float uDebugZ;
  uniform float uVibe;
  uniform vec3 uLight;
  uniform vec3 uLightCol;
  uniform float uCircle;
  uniform float uTime;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vP;
  varying vec4 vClip;
  ${MODE_CHUNK}

  float hash12(vec2 p) {
    vec3 p3 = fract(vec3(p.xyx) * 0.1031);
    p3 += dot(p3, p3.yzx + 33.33);
    return fract((p3.x + p3.y) * p3.z);
  }
  // brushed: fine streaks along x, several scales
  float brush(vec2 p) {
    float s = 0.0;
    s += hash12(vec2(floor(p.y * 900.0), 1.0)) * 0.5;
    s += hash12(vec2(floor(p.y * 260.0), 2.0)) * 0.3;
    s += hash12(vec2(floor(p.y * 70.0), floor(p.x * 3.0))) * 0.2;
    return s;
  }

  void main() {
    vec2 p = vP / ${PLATE_HALF.toFixed(1)};
    float rim;
    if (uCircle > 0.5) rim = smoothstep(1.0 - ${RIM_WIDTH.toFixed(3)}, 1.0 - ${RIM_WIDTH.toFixed(3)} + 0.004, length(p));
    else rim = smoothstep(1.0 - ${RIM_WIDTH.toFixed(3)}, 1.0 - ${RIM_WIDTH.toFixed(3)} + 0.004, max(abs(p.x), abs(p.y)));

    vec3 col;
    if (uDebugZ > 0.5) {
      col = vec3(min(1.0, modeAbs(p).x)) * 0.8;
    } else {
      // The vibration blur: near the edges, the brushing is read twice,
      // offset along the plate, while the sand is in the air.
      float edge = smoothstep(0.55, 1.0, max(abs(p.x), abs(p.y)));
      vec2 off = vec2(0.0, ${VIBE_BLUR.toFixed(3)} * uVibe * edge * sin(uTime * 37.0));
      float b = 0.5 * (brush(p + off) + brush(p - off));
      // anisotropic sheen: brightest where the streaks face the lamp
      float sheen = pow(max(0.0, 1.0 - abs(dot(normalize(vec2(uLight.x, uLight.z)), vec2(0.0, 1.0)))), 2.0);
      float ani = 0.6 + 0.8 * b;
      col = vec3(${PLATE_BASE.toFixed(3)}) * ani * (0.7 + 0.6 * sheen) * (0.85 + 0.3 * uLightCol);
      col = mix(col, vec3(0.11, 0.115, 0.12) * (0.8 + 0.4 * b), rim);
    }
    vec2 ndc = vClip.xy / max(vClip.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float att = mix(0.32, 1.0, smoothstep(0.0, uSafeFeather, d));
    col *= ${SAFE_FLOOR.toFixed(2)} + (att - 0.32) * (${((1 - SAFE_FLOOR) / 0.68).toFixed(5)});
    gl_FragColor = vec4(col, 1.0);
  }
`;

// The rod and the nut: dull steel, shaded by the same lamp.
const rodVertexShader = `
  precision mediump float;
  varying vec3 vN;
  void main() {
    vN = normalize(mat3(modelMatrix) * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;
const rodFragmentShader = `
  precision mediump float;
  uniform vec3 uLight;
  uniform vec3 uLightCol;
  varying vec3 vN;
  void main() {
    vec3 N = normalize(vN);
    float lam = max(0.0, dot(N, uLight));
    vec3 H = normalize(uLight + vec3(0.0, 1.0, 0.0));
    float spec = pow(max(0.0, dot(N, H)), 24.0);
    vec3 col = vec3(0.05, 0.052, 0.056) + vec3(0.18) * lam * uLightCol + vec3(0.35) * spec;
    gl_FragColor = vec4(col * 0.5, 1.0);
  }
`;

// =============================================================================

export class ChladniVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    const fm = urlParam('chladnimode');
    this._forcedMode = null;
    if (fm) {
      const t = fm.split(',').map(Number);
      if (t.length === 2 && t.every(Number.isFinite)) this._forcedMode = { n: t[0], m: t[1], circle: false };
    }
    this._debugZ = urlParam('chladniz') === '1';
    // ?chladnicircle=0 never takes the circular variant, =1 takes it on every far change
    const cc = urlParam('chladnicircle');
    this._circleChance = cc === '0' ? 0 : (cc === '1' ? 1 : CIRCLE_CHANCE);
    this._forceDrop = urlParam('chladnidrop') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._n = this._layout.n;
    this._count = this._n * this._n;
    this._time = 0;
    this._accumulator = 0;
    this._paused = false;
    this._simReady = false;
    this._beatKick = 0;
    this._disperse = 0;
    this._air = 0;
    this._airUntil = -1;
    this._vibe = 0;
    this._lowSmooth = 0.5;
    this._bands = { low: 0.4, mid: 0.4, high: 0.3 };
    this._modeIdx = 3;
    this._circle = false;
    this._circleIdx = 0;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrase = null;
    this._ownBeat = 0;
    this._beatsSeen = 0;
    this._lastDropAt = -1e9;
    this._lastPaletteRef = undefined;
    this._modeChanges = [];
    this._lightCol = new THREE.Color(1, 0.97, 0.9);
    this._lightDir = new THREE.Vector3(Math.cos(LIGHT_ELEV) * Math.cos(LIGHT_AZIMUTH), Math.sin(LIGHT_ELEV), Math.cos(LIGHT_ELEV) * Math.sin(LIGHT_AZIMUTH)).normalize();

    camera.position.set(0, CAMERA_DIST * Math.sin(CAMERA_PITCH), CAMERA_DIST * Math.cos(CAMERA_PITCH));
    camera.lookAt(0, 0, 0);
    if (camera.fov !== CAMERA_FOV) { camera.fov = CAMERA_FOV; camera.updateProjectionMatrix(); }

    this._buildSim();
    this._buildPlate();
    this._buildGrains();
    this._applyMode(this._forcedMode || { idx: this._modeIdx, circle: false });
  }

  // --- the simulation ----------------------------------------------------------
  _buildSim() {
    this._pos = new FeedbackBuffer({ width: this._n, height: this._n, type: THREE.FloatType, filter: THREE.NearestFilter });
    this._density = new THREE.WebGLRenderTarget(DENSITY_RES, DENSITY_RES, {
      type: THREE.UnsignedByteType, format: THREE.RGBAFormat, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
      depthBuffer: false, stencilBuffer: false, generateMipmaps: false
    });
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;
    this._simScene = new THREE.Scene();
    this._simQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial());
    this._simQuad.frustumCulled = false;
    this._simScene.add(this._simQuad);
    this._modeUniform = { value: new THREE.Vector4(1, 2, 0, 5) };
    this._stepUniforms = {
      uPos: { value: null }, uDensity: { value: this._density.texture }, uDt: { value: FIXED_DT }, uTime: { value: 0 },
      uForce: { value: FORCE }, uAgitation: { value: AGITATION }, uDisperse: { value: 0 }, uShiver: { value: 0 }, uRepel: { value: REPEL },
      uCircle: { value: 0 }, uMode: this._modeUniform
    };
    this._stepMat = new THREE.ShaderMaterial({ uniforms: this._stepUniforms, vertexShader: quadVertexShader, fragmentShader: stepFragmentShader, depthTest: false, depthWrite: false });

    // the density pass: one point per grain
    const idx = new Float32Array(this._count * 3);
    for (let i = 0; i < this._count; i++) {
      idx[i * 3] = ((i % this._n) + 0.5) / this._n;
      idx[i * 3 + 1] = (Math.floor(i / this._n) + 0.5) / this._n;
    }
    this._indexGeo = new THREE.BufferGeometry();
    this._indexGeo.setAttribute('position', new THREE.BufferAttribute(idx, 3));
    this._indexGeo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 10);
    this._densityUniforms = { uPos: { value: null } };
    this._densityMat = new THREE.ShaderMaterial({
      uniforms: this._densityUniforms, vertexShader: densityVertexShader, fragmentShader: densityFragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._densityScene = new THREE.Scene();
    this._densityPoints = new THREE.Points(this._indexGeo, this._densityMat);
    this._densityPoints.frustumCulled = false;
    this._densityScene.add(this._densityPoints);
    this._densityCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 10);
    this._densityCam.position.z = 1;
  }

  _seed(renderer) {
    const N = this._count;
    const data = new Float32Array(N * 4);
    for (let i = 0; i < N; i++) {
      data[i * 4] = (Math.random() * 2 - 1) * RIM;
      data[i * 4 + 1] = (Math.random() * 2 - 1) * RIM;
      data[i * 4 + 2] = Math.random();
      data[i * 4 + 3] = 1;
    }
    const tex = new THREE.DataTexture(data, this._n, this._n, THREE.RGBAFormat, THREE.FloatType);
    tex.needsUpdate = true;
    const copy = new THREE.ShaderMaterial({
      uniforms: { uTex: { value: tex } }, vertexShader: quadVertexShader,
      fragmentShader: 'precision highp float; uniform sampler2D uTex; varying vec2 vUv; void main(){ gl_FragColor = texture2D(uTex, vUv); }',
      depthTest: false, depthWrite: false
    });
    this._simQuad.material = copy;
    for (const rt of [this._pos.read, this._pos.write]) {
      renderer.setRenderTarget(rt);
      renderer.render(this._simScene, this._ortho);
    }
    copy.dispose(); tex.dispose();
  }

  _substep(renderer) {
    // density of the CURRENT positions
    this._densityUniforms.uPos.value = this._pos.read.texture;
    renderer.setRenderTarget(this._density);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, false, false);
    renderer.render(this._densityScene, this._densityCam);
    // the step
    this._simQuad.material = this._stepMat;
    this._stepUniforms.uPos.value = this._pos.read.texture;
    renderer.setRenderTarget(this._pos.write);
    renderer.render(this._simScene, this._ortho);
    this._pos.swap();
  }

  _runSim(renderer) {
    if (!this._simReady) {
      this._pos.ensureSize(renderer);
      this._seed(renderer);
      this._simReady = true;
    }
    const prev = renderer.getRenderTarget();
    const prevAuto = renderer.autoClear;
    renderer.autoClear = false;
    if (!this._paused) {
      let steps = 0;
      while (this._accumulator >= FIXED_DT && steps < MAX_SUBSTEPS) {
        this._stepUniforms.uTime.value = this._time - this._accumulator;
        this._substep(renderer);
        this._accumulator -= FIXED_DT;
        steps++;
      }
      if (this._accumulator > FIXED_DT * MAX_SUBSTEPS) this._accumulator = FIXED_DT * MAX_SUBSTEPS;
    }
    renderer.setRenderTarget(prev);
    renderer.autoClear = prevAuto;
    renderer.getDrawingBufferSize(_size);
    this._grainUniforms.uPixel.value = _size.y / 720;
    this._shadowUniforms.uPixel.value = _size.y / 720;
    this._grainUniforms.uPos.value = this._pos.read.texture;
    this._shadowUniforms.uPos.value = this._pos.read.texture;
  }

  // --- the scene -----------------------------------------------------------------
  _buildPlate() {
    const safe = {
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._plateUniforms = Object.assign({
      uDebugZ: { value: this._debugZ ? 1 : 0 }, uVibe: { value: 0 }, uLight: { value: this._lightDir },
      uLightCol: { value: new THREE.Vector3(1, 0.97, 0.9) }, uCircle: { value: 0 }, uTime: { value: 0 }, uMode: this._modeUniform
    }, safe);
    const mat = new THREE.ShaderMaterial({ uniforms: this._plateUniforms, vertexShader: plateVertexShader, fragmentShader: plateFragmentShader, side: THREE.DoubleSide });
    this._plateMat = mat;
    const sq = new THREE.PlaneGeometry(2 * PLATE_HALF, 2 * PLATE_HALF);
    sq.rotateX(-Math.PI / 2);
    this._plate = new THREE.Mesh(sq, mat);
    this._plate.renderOrder = -10;
    this._scene.add(this._plate);
    const circ = new THREE.CircleGeometry(PLATE_HALF, 72);
    circ.rotateX(-Math.PI / 2);
    this._plateCircle = new THREE.Mesh(circ, mat);
    this._plateCircle.renderOrder = -10;
    this._plateCircle.visible = false;
    this._scene.add(this._plateCircle);

    // the rod: through the plate, with a nut on top
    this._rodUniforms = { uLight: { value: this._lightDir }, uLightCol: { value: this._plateUniforms.uLightCol.value } };
    const rodMat = new THREE.ShaderMaterial({ uniforms: this._rodUniforms, vertexShader: rodVertexShader, fragmentShader: rodFragmentShader });
    this._rodMat = rodMat;
    const rod = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 0.8, 14), rodMat);
    rod.position.y = -0.4 + 0.03;
    const nut = new THREE.Mesh(new THREE.CylinderGeometry(0.065, 0.065, 0.035, 6), rodMat);
    nut.position.y = 0.0175 + 0.001;
    this._rod = new THREE.Group();
    this._rod.add(rod); this._rod.add(nut);
    this._scene.add(this._rod);
  }

  _buildGrains() {
    const safe = {
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    const common = () => Object.assign({
      uPos: { value: null }, uDensity: { value: this._density.texture }, uTime: { value: 0 }, uPixel: { value: 1 },
      uBounce: { value: BOUNCE_H }, uAgitation: { value: 0 }, uAir: { value: 0 },
      uLight: { value: this._lightDir }, uLightCol: { value: this._plateUniforms.uLightCol.value }, uMode: this._modeUniform
    }, safe);
    this._grainUniforms = common();
    this._grainMat = new THREE.ShaderMaterial({
      uniforms: this._grainUniforms, vertexShader: grainVertexShader(false), fragmentShader: grainFragmentShader,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._grains = new THREE.Points(this._indexGeo, this._grainMat);
    this._grains.frustumCulled = false;
    this._grains.renderOrder = 10;
    const self = this;
    this._grains.onBeforeRender = function (renderer) { self._runSim(renderer); };
    this._scene.add(this._grains);

    this._shadowUniforms = common();
    this._shadowMat = new THREE.ShaderMaterial({
      uniforms: this._shadowUniforms, vertexShader: grainVertexShader(true), fragmentShader: shadowFragmentShader,
      transparent: true, depthTest: false, depthWrite: false
    });
    this._shadows = new THREE.Points(this._indexGeo, this._shadowMat);
    this._shadows.frustumCulled = false;
    this._shadows.renderOrder = 5;
    this._shadows.visible = !!this._layout.shadows;
    // the shadows draw BEFORE the grains, and the sim runs on whichever draws first
    this._shadows.onBeforeRender = function (renderer) { self._runSim(renderer); };
    this._scene.add(this._shadows);
  }

  // --- the modes -------------------------------------------------------------------
  _applyMode(m) {
    if (m.n != null) {
      this._modeUniform.value.set(m.n, m.m, 0, 0);
      this._circle = false;
    } else if (m.circle) {
      const c = CIRCLE_MODES[m.idx % CIRCLE_MODES.length];
      this._modeUniform.value.set(c[0], 0, 1, c[1]);
      this._circle = true;
      this._circleIdx = m.idx;
    } else {
      const sq = MODES[Math.max(0, Math.min(MODES.length - 1, m.idx))];
      this._modeUniform.value.set(sq[0], sq[1], 0, 0);
      this._circle = false;
      this._modeIdx = Math.max(0, Math.min(MODES.length - 1, m.idx));
    }
    this._stepUniforms.uCircle.value = this._circle ? 1 : 0;
    this._plateUniforms.uCircle.value = this._circle ? 1 : 0;
    this._plate.visible = !this._circle;
    this._plateCircle.visible = this._circle;
  }

  // Where the spectrum wants the figure: bass -> simple modes, mids ->
  // middling, treble -> lace.
  _wantedIdx() {
    const b = this._bands;
    const tot = b.low + b.mid + b.high + 1e-6;
    const c = (b.mid * 0.5 + b.high * 1.0) / tot;      // 0 = all bass, 1 = all treble
    return Math.round(c * (MODES.length - 1));
  }

  _changeMode(far) {
    if (this._forcedMode) return;
    const want = this._wantedIdx();
    if (far) {
      // a far mode: at least three steps from the current one, toward the spectrum's wish
      let idx = want + Math.round((Math.random() - 0.5) * 4);
      if (Math.abs(idx - this._modeIdx) < 3) idx = this._modeIdx + (want >= this._modeIdx ? 3 : -3) + Math.round((Math.random() - 0.5) * 2);
      idx = Math.max(0, Math.min(MODES.length - 1, idx));
      if (this._layout.circle && Math.random() < this._circleChance && !this._circle) this._applyMode({ idx: (this._circleIdx + 1 + ((Math.random() * 3) | 0)), circle: true });
      else this._applyMode({ idx, circle: false });
      this._disperse = Math.max(this._disperse, DISPERSE_FAR);
    } else {
      if (this._circle) { this._applyMode({ idx: this._circleIdx + 1, circle: true }); }
      else {
        const dir = want > this._modeIdx ? 1 : (want < this._modeIdx ? -1 : (Math.random() < 0.5 ? -1 : 1));
        let idx = this._modeIdx + dir;
        if (idx < 0 || idx >= MODES.length) idx = this._modeIdx - dir;
        this._applyMode({ idx, circle: false });
      }
      this._disperse = Math.max(this._disperse, DISPERSE_NEAR);
    }
    this._modeChanges.push(this._time);
    if (this._modeChanges.length > 32) this._modeChanges.shift();
  }

  // --- the frame -------------------------------------------------------------------
  update(deltaTime, profile) {
    this._paused = profile.playbackState === 'PAUSED';
    if (this._paused) return;   // frozen: the grains hold their places
    const dt = Math.min(deltaTime, 0.05);
    this._time += dt;
    this._accumulator += dt;

    const director = profile.director || {};
    const bands = director.bands || null;
    const has = !!(bands && director.hasReliableGrid);
    const kb = 1 - Math.exp(-dt / 0.6);
    const b = this._bands;
    if (has) {
      b.low += (clamp01(bands.low) - b.low) * kb; b.mid += (clamp01(bands.mid) - b.mid) * kb; b.high += (clamp01(bands.high) - b.high) * kb;
    } else {
      // no analysis: a slow wander of the spectrum, so the library is visited
      b.low = 0.5 + 0.4 * Math.sin(this._time * 0.05); b.mid = 0.5 + 0.4 * Math.sin(this._time * 0.037 + 2.0); b.high = 0.5 + 0.4 * Math.sin(this._time * 0.029 + 4.0);
    }
    this._lowSmooth += ((has ? clamp01(bands.low) : 0.5 + 0.3 * Math.sin(this._time * 1.3)) - this._lowSmooth) * (1 - Math.exp(-dt / 0.3));
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 110;
    const beatS = 60 / Math.max(50, bpm);

    // --- the grid, or a beat of our own ----------------------------------------
    let newBeat = false, newBar = false, newPhrase = false;
    if (profile.beatPhase != null) {
      if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase - 0.5) newBeat = true;
      this._lastBeatPhase = profile.beatPhase;
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase - 0.5) newBar = true;
        this._lastBarPhase = profile.barPhase;
      } else if (newBeat && this._beatsSeen % 4 === 0) newBar = true;
      if (profile.phraseIndex != null) {
        if (this._lastPhrase != null && profile.phraseIndex !== this._lastPhrase) newPhrase = true;
        this._lastPhrase = profile.phraseIndex;
      } else if (newBeat && this._beatsSeen % 16 === 0 && this._beatsSeen > 0) newPhrase = true;
    } else {
      this._ownBeat += dt;
      if (this._ownBeat >= beatS) { this._ownBeat -= beatS; newBeat = true; }
      if (newBeat && this._beatsSeen % 4 === 0) newBar = true;
      if (newBeat && this._beatsSeen % 16 === 0 && this._beatsSeen > 0) newPhrase = true;
    }
    if (newBeat) this._beatsSeen++;

    // --- the palette: the lamp's colour, at 30% ------------------------------------
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const pal = profile.artworkPalette;
      const vivid = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors[0] : 0xffe0b0;
      const c = new THREE.Color(vivid);
      c.getHSL(_hsl);
      c.setHSL(_hsl.h, Math.min(0.9, _hsl.s), 0.6);
      this._lightCol.setRGB(lerp(1, c.r, PALETTE_IN_LIGHT), lerp(0.97, c.g, PALETTE_IN_LIGHT), lerp(0.9, c.b, PALETTE_IN_LIGHT));
      this._plateUniforms.uLightCol.value.set(this._lightCol.r, this._lightCol.g, this._lightCol.b);
    }

    // --- the drop: everything in the air for a beat, then the highest mode ---------
    const ev = director.nextEvent;
    const dropNow = (ev && ev.type === 'drop' && ev.msUntil != null && ev.msUntil <= 0 && this._time - this._lastDropAt > 4.0)
      || (this._forceDrop && this._time - this._lastDropAt > 8.0);
    if (dropNow) {
      this._lastDropAt = this._time;
      this._airUntil = this._time + beatS;
      this._dropPending = true;
    }
    const inAir = this._time < this._airUntil;
    if (!inAir && this._dropPending) {
      this._dropPending = false;
      if (!this._forcedMode) { this._applyMode({ idx: MODES.length - 1, circle: false }); this._modeChanges.push(this._time); }
      this._disperse = Math.max(this._disperse, DISPERSE_NEAR);
    }

    // --- the rhythm ----------------------------------------------------------------
    if (newPhrase && !inAir) this._changeMode(true);
    else if (newBar && !inAir) this._changeMode(false);
    if (newBeat) this._beatKick = 1;
    this._beatKick = Math.max(0, this._beatKick - dt * BEAT_DECAY * this._beatKick - dt * 0.2);
    this._disperse = Math.max(0, this._disperse - dt * DISPERSE_DECAY * this._disperse - dt * 0.1);
    this._air += ((inAir ? 1 : 0) - this._air) * Math.min(1, dt * 14);

    // --- the uniforms ----------------------------------------------------------------
    const agit = AGITATION * (1 + AGITATION_BEAT * this._beatKick) + DROP_AIR * this._air;
    const su = this._stepUniforms;
    su.uForce.value = FORCE * (1 + (FORCE_BEAT - 1) * this._beatKick) * (1 - 0.7 * this._air);
    su.uAgitation.value = agit;
    su.uDisperse.value = this._disperse + this._air * 1.2;
    su.uShiver.value = SHIVER * this._beatKick;
    const bounce = BOUNCE_H * (1 - BOUNCE_LOW * 0.5 + BOUNCE_LOW * this._lowSmooth);
    for (const u of [this._grainUniforms, this._shadowUniforms]) {
      u.uTime.value = this._time;
      u.uBounce.value = bounce;
      u.uAgitation.value = Math.min(1.5, 0.6 + 1.4 * this._beatKick + this._disperse);
      u.uAir.value = this._air * 1.5;
    }
    this._vibe += ((this._disperse > 0.15 || inAir ? 1 : 0) - this._vibe) * Math.min(1, dt * 10);
    this._plateUniforms.uVibe.value = this._vibe;
    this._plateUniforms.uTime.value = this._time;
  }

  // The verification's probe.
  get probe() {
    const m = this._modeUniform.value;
    return {
      mode: m.z > 0.5 ? { circle: true, n: m.x, k: m.w } : { n: m.x, m: m.y }, modeIdx: this._modeIdx, circle: this._circle,
      disperse: +this._disperse.toFixed(3), beatKick: +this._beatKick.toFixed(3), air: +this._air.toFixed(3),
      agitation: +this._stepUniforms.uAgitation.value.toFixed(3), force: +this._stepUniforms.uForce.value.toFixed(3),
      beatsSeen: this._beatsSeen, modeChanges: this._modeChanges.slice(-6).map((t) => +t.toFixed(2)), time: +this._time.toFixed(2),
      bands: { low: +this._bands.low.toFixed(2), mid: +this._bands.mid.toFixed(2), high: +this._bands.high.toFixed(2) }, n: this._n
    };
  }

  // Forces a mode change now (the harness).
  forceChange(far) { this._changeMode(!!far); }

  dispose() {
    this._scene.remove(this._grains); this._scene.remove(this._shadows);
    this._scene.remove(this._plate); this._scene.remove(this._plateCircle); this._scene.remove(this._rod);
    this._indexGeo.dispose();
    this._grainMat.dispose(); this._shadowMat.dispose();
    this._plate.geometry.dispose(); this._plateCircle.geometry.dispose(); this._plateMat.dispose();
    for (const c of this._rod.children) c.geometry.dispose();
    this._rodMat.dispose();
    this._simQuad.geometry.dispose(); this._stepMat.dispose(); this._densityMat.dispose();
    this._pos.dispose(); this._density.dispose();
  }
}

const _size = new THREE.Vector2();
const _hsl = { h: 0, s: 0, l: 0 };
